// src/ProcessorWrapper.ts
var ProcessorWrapper = class {
  static get isSupported() {
    return typeof MediaStreamTrackGenerator !== "undefined" && typeof MediaStreamTrackProcessor !== "undefined";
  }
  constructor(transformer, name) {
    this.name = name;
    this.transformer = transformer;
  }
  async setup(opts) {
    var _a, _b, _c, _d;
    this.source = opts.track;
    const origConstraints = this.source.getConstraints();
    await this.source.applyConstraints({
      ...origConstraints,
      // @ts-expect-error when a mediastream track is resized and/or cropped, the `VideoFrame` will have a coded height/width of the original video size
      // this leads to a shift of the underlying video as the frame itself is being rendered with the coded size
      // but image segmentation is based on the display dimensions (-> the cropped version)
      // in order to prevent this, we force the resize mode to "none"
      resizeMode: "none"
    });
    this.sourceSettings = this.source.getSettings();
    this.sourceDummy = opts.element;
    if (!(this.sourceDummy instanceof HTMLVideoElement)) {
      throw TypeError("Currently only video transformers are supported");
    }
    if (this.sourceDummy instanceof HTMLVideoElement) {
      this.sourceDummy.height = (_a = this.sourceSettings.height) != null ? _a : 300;
      this.sourceDummy.width = (_b = this.sourceSettings.width) != null ? _b : 300;
    }
    this.processor = new MediaStreamTrackProcessor({ track: this.source });
    this.trackGenerator = new MediaStreamTrackGenerator({
      kind: "video",
      signalTarget: this.source
    });
    this.canvas = new OffscreenCanvas(
      (_c = this.sourceSettings.width) != null ? _c : 300,
      (_d = this.sourceSettings.height) != null ? _d : 300
    );
  }
  async init(opts) {
    await this.setup(opts);
    if (!this.canvas || !this.processor || !this.trackGenerator) {
      throw new TypeError("Expected both canvas and processor to be defined after setup");
    }
    const readableStream = this.processor.readable;
    await this.transformer.init({
      outputCanvas: this.canvas,
      inputElement: this.sourceDummy
    });
    const pipedStream = readableStream.pipeThrough(this.transformer.transformer);
    pipedStream.pipeTo(this.trackGenerator.writable).catch((e) => console.error("error when trying to pipe", e)).finally(() => this.destroy());
    this.processedTrack = this.trackGenerator;
  }
  async restart(opts) {
    await this.destroy();
    return this.init(opts);
  }
  async restartTransformer(...options) {
    this.transformer.restart(options[0]);
  }
  async updateTransformerOptions(...options) {
    this.transformer.update(options[0]);
  }
  async destroy() {
    var _a;
    await this.transformer.destroy();
    (_a = this.trackGenerator) == null ? void 0 : _a.stop();
  }
};

// src/transformers/BackgroundTransformer.ts
import * as vision from "@mediapipe/tasks-vision";

// package.json
var dependencies = {
  "@mediapipe/holistic": "0.5.1675471629",
  "@mediapipe/tasks-vision": "0.10.9"
};

// src/transformers/VideoTransformer.ts
var VideoTransformer = class {
  constructor() {
    this.isDisabled = false;
  }
  async init({
    outputCanvas,
    inputElement: inputVideo
  }) {
    var _a;
    if (!(inputVideo instanceof HTMLVideoElement)) {
      throw TypeError("Video transformer needs a HTMLVideoElement as input");
    }
    this.transformer = new TransformStream({
      transform: (frame, controller) => this.transform(frame, controller)
    });
    this.canvas = outputCanvas || null;
    if (outputCanvas) {
      this.ctx = ((_a = this.canvas) == null ? void 0 : _a.getContext("2d")) || void 0;
    }
    this.inputVideo = inputVideo;
    this.isDisabled = false;
  }
  async restart({ outputCanvas, inputElement: inputVideo }) {
    this.canvas = outputCanvas || null;
    this.ctx = this.canvas.getContext("2d") || void 0;
    this.inputVideo = inputVideo;
    this.isDisabled = false;
  }
  async destroy() {
    this.isDisabled = true;
    this.canvas = void 0;
    this.ctx = void 0;
  }
};

// src/transformers/BackgroundTransformer.ts
var BackgroundProcessor = class extends VideoTransformer {
  constructor(opts) {
    super();
    this.backgroundImage = null;
    this.options = opts;
    this.update(opts);
  }
  static get isSupported() {
    return typeof OffscreenCanvas !== "undefined";
  }
  async init({ outputCanvas, inputElement: inputVideo }) {
    var _a, _b, _c, _d, _e;
    await super.init({ outputCanvas, inputElement: inputVideo });
    const fileSet = await vision.FilesetResolver.forVisionTasks(
      (_b = (_a = this.options.assetPaths) == null ? void 0 : _a.tasksVisionFileSet) != null ? _b : `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${dependencies["@mediapipe/tasks-vision"]}/wasm`
    );
    this.imageSegmenter = await vision.ImageSegmenter.createFromOptions(fileSet, {
      baseOptions: {
        modelAssetPath: (_d = (_c = this.options.assetPaths) == null ? void 0 : _c.modelAssetPath) != null ? _d : "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite",
        delegate: "GPU",
        ...this.options.segmenterOptions
      },
      runningMode: "VIDEO",
      outputCategoryMask: true,
      outputConfidenceMasks: false
    });
    if (((_e = this.options) == null ? void 0 : _e.imagePath) && !this.backgroundImage) {
      await this.loadBackground(this.options.imagePath).catch(
        (err) => console.error("Error while loading processor background image: ", err)
      );
    }
  }
  async destroy() {
    var _a;
    await super.destroy();
    await ((_a = this.imageSegmenter) == null ? void 0 : _a.close());
    this.backgroundImage = null;
  }
  async loadBackground(path) {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.crossOrigin = "Anonymous";
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = path;
    });
    const imageData = await createImageBitmap(img);
    this.backgroundImage = imageData;
  }
  async transform(frame, controller) {
    var _a;
    try {
      if (!(frame instanceof VideoFrame)) {
        console.debug("empty frame detected, ignoring");
        return;
      }
      if (this.isDisabled) {
        controller.enqueue(frame);
        return;
      }
      if (!this.canvas) {
        throw TypeError("Canvas needs to be initialized first");
      }
      let startTimeMs = performance.now();
      (_a = this.imageSegmenter) == null ? void 0 : _a.segmentForVideo(
        this.inputVideo,
        startTimeMs,
        (result) => this.segmentationResults = result
      );
      if (this.blurRadius) {
        await this.blurBackground(frame);
      } else {
        await this.drawVirtualBackground(frame);
      }
      const newFrame = new VideoFrame(this.canvas, {
        timestamp: frame.timestamp || Date.now()
      });
      controller.enqueue(newFrame);
    } finally {
      frame == null ? void 0 : frame.close();
    }
  }
  async update(opts) {
    this.options = opts;
    if (opts.blurRadius) {
      this.blurRadius = opts.blurRadius;
    } else if (opts.imagePath) {
      await this.loadBackground(opts.imagePath);
    }
  }
  async drawVirtualBackground(frame) {
    var _a;
    if (!this.canvas || !this.ctx || !this.segmentationResults || !this.inputVideo)
      return;
    if ((_a = this.segmentationResults) == null ? void 0 : _a.categoryMask) {
      this.ctx.filter = "blur(10px)";
      this.ctx.globalCompositeOperation = "copy";
      const bitmap = await maskToBitmap(
        this.segmentationResults.categoryMask,
        this.segmentationResults.categoryMask.width,
        this.segmentationResults.categoryMask.height
      );
      this.ctx.drawImage(bitmap, 0, 0, this.canvas.width, this.canvas.height);
      this.ctx.filter = "none";
      this.ctx.globalCompositeOperation = "source-in";
      if (this.backgroundImage) {
        this.ctx.drawImage(
          this.backgroundImage,
          0,
          0,
          this.backgroundImage.width,
          this.backgroundImage.height,
          0,
          0,
          this.canvas.width,
          this.canvas.height
        );
      } else {
        this.ctx.fillStyle = "#00FF00";
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      }
      this.ctx.globalCompositeOperation = "destination-over";
    }
    this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);
  }
  async blurBackground(frame) {
    var _a, _b;
    if (!this.ctx || !this.canvas || !((_b = (_a = this.segmentationResults) == null ? void 0 : _a.categoryMask) == null ? void 0 : _b.canvas) || !this.inputVideo) {
      return;
    }
    this.ctx.save();
    this.ctx.globalCompositeOperation = "copy";
    const bitmap = await maskToBitmap(
      this.segmentationResults.categoryMask,
      this.segmentationResults.categoryMask.width,
      this.segmentationResults.categoryMask.height
    );
    this.ctx.filter = "blur(3px)";
    this.ctx.globalCompositeOperation = "copy";
    this.ctx.drawImage(bitmap, 0, 0, this.canvas.width, this.canvas.height);
    this.ctx.filter = "none";
    this.ctx.globalCompositeOperation = "source-out";
    this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);
    this.ctx.globalCompositeOperation = "destination-over";
    this.ctx.filter = `blur(${this.blurRadius}px)`;
    this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();
  }
};
function maskToBitmap(mask, videoWidth, videoHeight) {
  const dataArray = new Uint8ClampedArray(videoWidth * videoHeight * 4);
  const result = mask.getAsUint8Array();
  for (let i = 0; i < result.length; i += 1) {
    dataArray[i * 4] = result[i];
    dataArray[i * 4 + 1] = result[i];
    dataArray[i * 4 + 2] = result[i];
    dataArray[i * 4 + 3] = result[i];
  }
  const dataNew = new ImageData(dataArray, videoWidth, videoHeight);
  return createImageBitmap(dataNew);
}

// src/index.ts
var BackgroundBlur = (blurRadius = 10, segmenterOptions) => {
  return BackgroundProcessor2({ blurRadius, segmenterOptions }, "background-blur");
};
var VirtualBackground = (imagePath, segmenterOptions) => {
  return BackgroundProcessor2({ imagePath, segmenterOptions }, "virtual-background");
};
var BackgroundProcessor2 = (options, name = "background-processor") => {
  const isProcessorSupported = ProcessorWrapper.isSupported && BackgroundProcessor.isSupported;
  if (!isProcessorSupported) {
    throw new Error("processor is not supported in this browser");
  }
  const processor = new ProcessorWrapper(new BackgroundProcessor(options), name);
  return processor;
};
export {
  BackgroundBlur,
  BackgroundProcessor2 as BackgroundProcessor,
  BackgroundProcessor as BackgroundTransformer,
  ProcessorWrapper,
  VideoTransformer,
  VirtualBackground
};
//# sourceMappingURL=index.mjs.map